import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Upload, ShieldCheck, Stamp, Archive, Download, CheckCircle2, AlertTriangle, PencilLine, Undo2, BookOpen } from "lucide-react";
import { EditDialog } from "@/components/RecordEditor";

const STATUS_BADGE = {
  Draft: "bg-slate-200 text-slate-800",
  "In review": "bg-sky-700 text-white",
  "Pending approval": "bg-amber-600 text-white",
  Effective: "bg-[#134e4a] text-white",
  Superseded: "bg-slate-300 text-slate-800",
  Retired: "bg-slate-400 text-white",
};

const Section = ({ title, children, testid, action }) => (
  <section className="border border-slate-200" data-testid={testid}>
    <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
      <h2 className="text-base font-semibold text-slate-800 md:text-lg">{title}</h2>
      {action}
    </header>
    <div className="p-3">{children}</div>
  </section>
);

export default function DocumentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [changeSummary, setChangeSummary] = useState("");
  const [busy, setBusy] = useState("");
  const [retireOpen, setRetireOpen] = useState(false);
  const [retireReason, setRetireReason] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [viewer, setViewer] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/documents/${id}`);
      setData(d);
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/documents/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-sm text-slate-500" data-testid="document-detail-loading">Loading document…</div>;
  const d = data.document;
  const canAuthor = ["admin", "lab_director", "supervisor", "pathologist", "pathologist_assistant", "tech"].includes(user?.role);
  const canReview = ["admin", "lab_director", "supervisor"].includes(user?.role);
  const canApprove = ["admin", "pathologist"].includes(user?.role);
  const canRetire = ["admin", "lab_director", "supervisor"].includes(user?.role);
  const pending = data.versions.find((v) => v.status === "Draft" || v.status === "Pending approval");

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first");
    setBusy("upload");
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/documents/${id}/versions?change_summary=${encodeURIComponent(changeSummary)}`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Revision uploaded — ready for technical review");
      setFile(null);
      setChangeSummary("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const act = async (path, label, payload = {}) => {
    setBusy(label);
    try {
      await api.post(path, payload);
      toast.success(label);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const download = async (version, { forceSave = false } = {}) => {
    try {
      const res = await api.get(`/documents/${id}/versions/${version}/file`, { responseType: "blob" });
      const rev = (data?.versions || []).find((v) => v.version === version);
      const filename = rev?.filename || `${d?.control_number || "document"}-rev${version}`;
      const type = res.data.type || rev?.content_type || "";
      const url = URL.createObjectURL(res.data);
      const inline = /pdf|text|image/i.test(type) || /\.(pdf|txt|png|jpe?g)$/i.test(filename);
      if (inline && !forceSave) {
        setViewer({ url, filename, version, type });
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const closeViewer = () => {
    if (viewer?.url) URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  const retire = async () => {
    if (retireReason.trim().length < 3) return toast.error("Give a reason for retiring this document");
    setBusy("retire");
    try {
      await api.post(`/documents/${id}/retire`, { reason: retireReason });
      toast.success("Document retired");
      setRetireOpen(false);
      setRetireReason("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="document-detail-page">
      <Link to="/documents" className="inline-flex items-center gap-2 text-sm font-semibold text-[#134e4a] hover:underline" data-testid="back-to-documents">
        <ArrowLeft size={15} /> All documents
      </Link>

      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-3xl font-bold tracking-tight text-slate-900" data-testid="doc-control-number">{d.control_number}</h1>
            <span className={`px-2 py-0.5 text-[11px] font-bold uppercase ${STATUS_BADGE[d.status]}`} data-testid="doc-status-badge">{d.status}</span>
            {d.current_version > 0 && (
              <span className="num bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700" data-testid="doc-current-rev">Rev {d.current_version}</span>
            )}
            {d.review_overdue && (
              <span className="bg-red-800 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="doc-overdue-badge">Review overdue</span>
            )}
            {d.pending_status && (
              <span className="bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900" data-testid="doc-pending-badge">
                Rev {d.pending_version} {d.pending_status.toLowerCase()}
              </span>
            )}
          </div>
          <p className="mt-1 text-lg font-semibold text-slate-800">{d.title}</p>
          <p className="mt-1 text-sm text-slate-600">
            {d.doc_type} · {d.department} · Author {d.author || "—"}
            {d.approved_by ? ` · Approved by ${d.approved_by}` : ""}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Effective {d.effective_date ? fmtDateTime(d.effective_date) : "—"} · Next review{" "}
            {d.next_review_date ? fmtDateTime(d.next_review_date) : "—"} · Every {d.review_interval_months} months
          </p>
          {(d.keywords || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {d.keywords.map((k) => <span key={k} className="bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">{k}</span>)}
            </div>
          )}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {d.current_version > 0 ? (
            <Button variant="outline" onClick={() => download(d.current_version)} data-testid="download-current-button" className="h-11 border-slate-300">
              <BookOpen size={15} className="mr-2" /> Read Rev {d.current_version}
            </Button>
          ) : (
            <span className="inline-flex h-11 items-center bg-amber-50 px-3 text-sm text-amber-900" data-testid="no-file-yet">
              No file uploaded yet — add the procedure below
            </span>
          )}
          {d.status === "Effective" && d.requires_acknowledgement && (
            data.acknowledged_by_me ? (
              <span className="inline-flex h-11 items-center gap-2 bg-green-50 px-3 text-sm font-semibold text-green-900" data-testid="ack-confirmed">
                <CheckCircle2 size={15} /> You’ve signed this revision
              </span>
            ) : (
              <Button onClick={() => act(`/documents/${id}/acknowledge`, "Read and understood recorded")}
                      disabled={busy === "Read and understood recorded"} data-testid="acknowledge-button"
                      className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <ShieldCheck size={15} className="mr-2" /> Mark read &amp; understood
              </Button>
            )
          )}
          {canAuthor && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="correct-document-button"
                    className="h-11 border-slate-300">
              <PencilLine size={15} className="mr-2" /> Correct details
            </Button>
          )}
          {canRetire && d.status === "Retired" && (
            <Button variant="outline" disabled={busy === "Document reinstated"}
                    onClick={() => act(`/documents/${id}/restore`, "Document reinstated")}
                    data-testid="restore-document-button" className="h-11 border-slate-300">
              <Undo2 size={15} className="mr-2" /> Reinstate
            </Button>
          )}
          {canRetire && d.status !== "Retired" && (
            <Button variant="outline" onClick={() => setRetireOpen(true)} data-testid="retire-button"
                    className="h-11 border-red-300 text-red-800 hover:bg-red-50">
              <Archive size={15} className="mr-2" /> Retire
            </Button>
          )}
        </div>
      </div>

      {d.description && (
        <Section title="Purpose & scope" testid="doc-description">
          <p className="whitespace-pre-wrap text-sm text-slate-700">{d.description}</p>
        </Section>
      )}

      {canAuthor && (
        <Section title="Assistant search" testid="doc-index">
          <p className="text-sm text-slate-600">
            {d.indexed_chars
              ? `Indexed — ${d.indexed_chars.toLocaleString()} characters of rev ${d.indexed_version} are searchable, so Silas can quote this procedure.`
              : d.index_note
              ? d.index_note
              : "Not indexed yet. Index the PDF so the assistant can answer from inside this procedure, not just its title."}
          </p>
          <Button size="sm" disabled={busy === "index"} data-testid="doc-index-button"
                  className="mt-2 h-9 bg-[#134e4a] hover:bg-[#0f3f3c]"
                  onClick={async () => {
                    setBusy("index");
                    try {
                      const { data: r } = await api.post(`/documents/${id}/index`);
                      toast[r.indexed ? "success" : "warning"](
                        r.indexed ? `Indexed ${r.chars.toLocaleString()} characters` : r.reason);
                      load();
                    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
                  }}>
            {busy === "index" ? "Indexing…" : d.indexed_chars ? "Re-index this document" : "Index for the assistant"}
          </Button>
        </Section>
      )}

      {canAuthor && d.status !== "Retired" && (
        <Section title={pending ? `Rev ${pending.version} is in the review cycle` : "Upload a new revision"} testid="doc-upload">
          {pending ? (
            <p className="text-sm text-slate-600">
              Finish the review cycle on Rev {pending.version} before uploading another revision.
            </p>
          ) : (
            <form onSubmit={upload} className="flex flex-wrap items-end gap-3">
              <div className="min-w-[240px] flex-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">File (PDF, DOCX, XLSX, TXT)</Label>
                <Input type="file" accept=".pdf,.doc,.docx,.txt,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
                       data-testid="doc-file-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div className="min-w-[240px] flex-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Change summary</Label>
                <Input value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)}
                       data-testid="doc-change-summary-input" placeholder="What changed in this revision?"
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <Button type="submit" disabled={busy === "upload"} data-testid="upload-revision-button"
                      className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Upload size={15} className="mr-2" />{busy === "upload" ? "Uploading…" : "Upload revision"}
              </Button>
            </form>
          )}
        </Section>
      )}

      <Section title={`Revision history (${data.versions.length})`} testid="doc-versions">
        {data.versions.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="doc-versions-empty">No revisions uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {data.versions.map((v) => (
              <div key={v.id} className="border border-slate-200 p-3" data-testid={`doc-version-${v.version}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="num font-semibold text-slate-900">Rev {v.version}</span>
                  <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[v.status] || "bg-slate-200"}`}>{v.status}</span>
                  <span className="text-xs text-slate-500">{v.filename} · {Math.round((v.size || 0) / 1024)} KB · uploaded by {v.uploaded_by} {fmtDateTime(v.created_at)}</span>
                  <div className="ml-auto flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => download(v.version)}
                            data-testid={`doc-download-${v.version}`} className="h-9 border-slate-300">
                      <BookOpen size={14} className="mr-1" /> Read
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => download(v.version, { forceSave: true })}
                            data-testid={`doc-save-${v.version}`} className="h-9 border-slate-300">
                      <Download size={14} />
                    </Button>
                    {v.status === "Draft" && canReview && (
                      <Button size="sm" disabled={busy === "Technical review recorded"}
                              onClick={() => act(`/documents/${id}/versions/${v.version}/review`, "Technical review recorded")}
                              data-testid={`doc-review-${v.version}`} className="h-9 bg-sky-800 hover:bg-sky-900">
                        <ShieldCheck size={14} className="mr-1" /> Technical review
                      </Button>
                    )}
                    {v.status === "Pending approval" && canApprove && (
                      <Button size="sm" disabled={busy === "Approved and made effective"}
                              onClick={() => act(`/documents/${id}/versions/${v.version}/approve`, "Approved and made effective")}
                              data-testid={`doc-approve-${v.version}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                        <Stamp size={14} className="mr-1" /> Medical director approval
                      </Button>
                    )}
                  </div>
                </div>
                {v.change_summary && <p className="mt-2 text-sm text-slate-700">{v.change_summary}</p>}
                <div className="mt-2 grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
                  <div data-testid={`doc-review-stamp-${v.version}`}>
                    Technical review: {v.technical_review
                      ? `${v.technical_review.by} · ${fmtDateTime(v.technical_review.at)}${v.technical_review.note ? ` — ${v.technical_review.note}` : ""}`
                      : "pending"}
                  </div>
                  <div data-testid={`doc-approval-stamp-${v.version}`}>
                    Approval: {v.approval
                      ? `${v.approval.by} · ${fmtDateTime(v.approval.at)}${v.approval.note ? ` — ${v.approval.note}` : ""}`
                      : "pending"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {d.requires_acknowledgement && d.current_version > 0 && (
        <Section title="Read & understood" testid="doc-acks">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Signed Rev {d.current_version}</h3>
              <ul className="mt-2 space-y-1 text-sm">
                {data.acknowledgements.filter((a) => a.version === d.current_version).map((a) => (
                  <li key={a.user_id} className="flex items-center gap-2 text-slate-700" data-testid={`ack-${a.user_id}`}>
                    <CheckCircle2 size={14} className="text-green-800" /> {a.user_name}
                    <span className="ml-auto text-xs text-slate-500">{fmtDateTime(a.at)}</span>
                  </li>
                ))}
                {data.acknowledgements.filter((a) => a.version === d.current_version).length === 0 && (
                  <li className="text-sm text-slate-500">Nobody has signed this revision yet.</li>
                )}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Still outstanding</h3>
              <ul className="mt-2 space-y-1 text-sm">
                {data.outstanding_readers.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 text-slate-700" data-testid={`outstanding-${s.id}`}>
                    <AlertTriangle size={14} className="text-amber-600" /> {s.name}
                    <span className="ml-auto text-xs uppercase text-slate-500">{s.role}</span>
                  </li>
                ))}
                {data.outstanding_readers.length === 0 && (
                  <li className="text-sm text-green-900">Everyone on staff has signed this revision.</li>
                )}
              </ul>
            </div>
          </div>
        </Section>
      )}

      {d.status === "Retired" && (
        <p className="border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700" data-testid="doc-retired-note">
          Retired by {d.retired_by} on {fmtDateTime(d.retired_at)} — {d.retired_reason}. The full revision history stays on file.
        </p>
      )}

      <EditDialog
        open={editOpen} onOpenChange={setEditOpen} testid="correct-document-dialog"
        title={`Correct ${d.control_number}`}
        description="Fix a wrong control number, type or title. The revision history and approvals stay untouched."
        values={d}
        fields={[
          { name: "control_number", label: "Control number", hint: "Must be unique across PolicyTraX" },
          { name: "doc_type", label: "Document type", type: "select", options: meta?.doc_types || [] },
          { name: "title", label: "Title", span: 2 },
          { name: "department", label: "Department", type: "select", options: meta?.departments || [] },
          { name: "review_interval_months", label: "Review interval (months)", type: "number" },
          { name: "description", label: "Purpose & scope", type: "textarea", span: 2 },
        ]}
        onSave={async (body) => {
          await api.put(`/documents/${id}`, body);
          toast.success("Document corrected");
          load();
        }}
      />

      <Dialog open={!!viewer} onOpenChange={(v) => !v && closeViewer()}>
        <DialogContent className="h-[92vh] max-w-6xl p-0" data-testid="document-viewer-dialog">
          <DialogHeader className="border-b border-slate-200 px-4 py-3">
            <DialogTitle className="text-base">
              {d.control_number} — Rev {viewer?.version}
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-3">
              <span>{viewer?.filename}</span>
              <a href={viewer?.url} download={viewer?.filename} data-testid="viewer-save-link"
                 className="font-semibold text-[#134e4a] hover:underline">Save a copy</a>
              <a href={viewer?.url} target="_blank" rel="noreferrer" data-testid="viewer-newtab-link"
                 className="font-semibold text-[#134e4a] hover:underline">Open in a new tab</a>
              <span className="text-xs text-slate-500">Nothing showing? Use "Open in a new tab".</span>
            </DialogDescription>
          </DialogHeader>
          {viewer && (
            <iframe title="Procedure" src={viewer.url} data-testid="document-viewer-frame"
                    className="h-full w-full border-0 bg-slate-100" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={retireOpen} onOpenChange={setRetireOpen}>
        <DialogContent data-testid="retire-dialog">
          <DialogHeader>
            <DialogTitle>Retire {d.control_number}</DialogTitle>
            <DialogDescription>
              Retiring stops this document being effective. Nothing is deleted — the revision history stays on file.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Nothing is deleted — the full revision history stays on file for inspectors.
          </p>
          <Textarea value={retireReason} onChange={(e) => setRetireReason(e.target.value)}
                    data-testid="retire-reason-input" placeholder="Reason — e.g. superseded by PTX-SOP-0042"
                    className="min-h-[90px] border-slate-300" />
          <Button onClick={retire} disabled={busy === "retire"} data-testid="confirm-retire-button"
                  className="h-11 bg-red-800 hover:bg-red-900">
            {busy === "retire" ? "Retiring…" : "Retire document"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
